import { ActionTypes, ProductFieldValidation, UserFlags } from './types';
import * as apiActions from '@configuratorware/configurator-frontendgui/App/Reducers/Actions';
import * as configuratorActions from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import Api from '@configuratorware/configurator-frontendgui/Framework/Api';
import { withGlobalLoader } from '@configuratorware/configurator-frontendgui/App/Reducers/UI/Actions';

import {
  getNextStep,
  getSelectedProductIdentifier,
  isProductParametersStep,
  isSystemParametersStep,
  getCustomData,
  isProductTypeSelectionStep,
  isSystemSelectionStep,
  getSelectedProductType,
  getProductParameterFormData,
  isProductParameterWarningsApproved,
  isSurroundingsMode,
  isConfiguratorStep,
  Steps,
} from './Selectors';
import { mapMenuApiToState } from './menuApiMapping';
import { ConfiguratorComponent } from '../../Components/Forms/types';
import { resetConfiguration, setCustomData } from '../Configurator/Actions';
import { checkRule } from '../../Components/FormBuilder/ruleChecker';
import { RuleCheckItem } from '../../Components/FormBuilder/types';
import { addInitialInstance } from '../MachineInstance/Actions';
import { cachedPromiseCreator } from '../../Helpers/CachedPromise';

export const setCurrentStep = (payload: string) => (dispatch, getState) => {
  if (isProductParametersStep(getState()) && getNextStep(getState()) === payload) {
    if (!dispatch(checkProductParameterRules())) {
      return;
    }
  }

  dispatch({
    type: ActionTypes.SET_CURRENT_STEP,
    payload,
  });

  if (isSystemSelectionStep(getState())) {
    dispatch(setSelectedSystem(null));
    dispatch(resetConfiguration());
  }

  if (isProductTypeSelectionStep(getState())) {
    dispatch(resetConfiguration());
  }

  if (isProductParametersStep(getState())) {
    dispatch(resetProductParameterWarnings());
    dispatch(setSelectedConfiguratorMenu(null));
  }

  if (isSystemParametersStep(getState())) {
    const identifier = getSelectedProductIdentifier(getState());
    const currentIdentifier = getState().configurator.configuration?.item?.identifier;
    if (currentIdentifier !== identifier) {
      dispatch(setSystemDataOptions([]));
      dispatch(configuratorActions.setLoadIdentifier(identifier));
      dispatch(withGlobalLoader(configuratorActions.fetchInitialConfiguration()));
      dispatch(withGlobalLoader(loadSystemDataForSelectedProduct()));
    }
  }

  if (isConfiguratorStep(getState())) {
    dispatch(withGlobalLoader(loadConfiguratorMenuOptions()));
  }

  if (payload === Steps.SYSTEM_SELECT && !getState().ipco.systems?.length) {
    dispatch(withGlobalLoader(loadMenu()));
  }
};

const receiveMenu = (data: unknown) => ({
  type: ActionTypes.SET_MENU,
  payload: mapMenuApiToState(data),
});

export const loadMenu = () => (dispatch) => {
  return dispatch(apiActions.fetchDataAsync('menu', receiveMenu));
};

const checkProductParameterRules = () => (dispatch, getState) => {
  if (isProductParameterWarningsApproved(getState())) {
    return true;
  }
  const { rules } = getProductParameterFormData(getState());
  if (!rules?.length) {
    return true;
  }
  const customData = getCustomData(getState());
  const warnings = rules.map((rule) => checkRule(rule, customData)).filter((result) => result !== true);
  dispatch(setProductParameterWarnings(warnings));
  return warnings.length === 0;
};

export const setProductParameterWarnings = (warnings: RuleCheckItem[]) => ({
  type: ActionTypes.SET_PRODUCT_PARAMETER_WARNINGS,
  warnings,
});

export const resetProductParameterWarnings = () => setProductParameterWarnings([]);

export const setProductParameterWarningsApproved = () => ({
  type: ActionTypes.SET_PRODUCT_PARAMETER_WARNINGS_APPROVED,
});

export const setSelectedSystem = (payload: string) => (dispatch) => {
  dispatch(configuratorActions.setLoadIdentifier(null));
  dispatch({
    type: ActionTypes.SET_SELECTED_SYSTEM,
    payload,
  });
};

export const setSelectedProductType = (payload: string | null) => ({
  type: ActionTypes.SET_SELECTED_PRODUCT_TYPE,
  payload,
});

export const setSystemDataOptions = (payload: object | null) => ({
  type: ActionTypes.SET_SYSTEM_DATA_OPTIONS,
  payload,
});

export const setProductParamsValid = (payload: ProductFieldValidation | false) => ({
  type: ActionTypes.SET_PRODUCT_PARAM_VALID,
  payload,
});

export const setConfiguratorMenuOptions = (payload: object | null) => (dispatch, getState) => {
  dispatch({
    type: ActionTypes.SET_CONFIGURATOR_MENU_OPTIONS,
    payload,
  });
  dispatch(setInitialSurroundingData(getState()));
};

export const setLanguages = (payload: object | null) => (dispatch, getState) => {
  dispatch({
    type: ActionTypes.SET_LANGUAGES,
    payload,
  });
};

export const setCountries = (payload: object | null) => (dispatch, getState) => {
  dispatch({
    type: ActionTypes.SET_COUNTRIES,
    payload,
  });
};

const setInitialSurroundingData = (state) =>
  setCustomData(
    state.ipco.configuratorMenuOptions
      .find(({ identifier }) => identifier === 'surroundings')
      ?.elements?.map(({ identifier, options }) => {
        if (state.configurator.configuration.customdata[identifier]) {
          // skip if already set
          return {};
        }
        const firstOption = options && options[0];
        const firstAttr = firstOption?.attributes && firstOption.attributes[0];
        const minValue = firstAttr?.identifier === 'range' && parseInt(firstAttr.value.match(/^\d+/)[0]);
        const value = minValue || firstOption?.identifier;
        return value ? { [identifier]: value } : {};
      })
      .reduce((result, current) => ({ ...result, ...current }), {})
  );

export const setSelectedConfiguratorMenu = (payload: string) => (dispatch) => {
  isSurroundingsMode(payload) && dispatch(addInitialInstance());
  dispatch({
    type: ActionTypes.SET_SELECTED_CONFIGURATOR_MENU,
    payload,
  });
};

export const setSelectedComponent = (payload: ConfiguratorComponent | null) => ({
  type: ActionTypes.SET_SELECTED_COMPONENT,
  payload,
});

export const loadSystemDataForSelectedProduct = () => (dispatch, getState) => {
  const identifier = getSelectedProductIdentifier(getState());
  dispatch(apiActions.fetchDataAsync(`items/${identifier}/system-data`, setSystemDataOptions));
};

export const loadConfiguratorMenuOptions = () => (dispatch, getState) => {
  const identifier = getSelectedProductIdentifier(getState());
  const customData = getCustomData(getState());
  return dispatch(
    apiActions.postData(
      `configurations/menu`,
      {
        itemIdentifier: identifier,
        width: parseInt(customData.width),
        length: parseFloat(customData.length),
      },
      setConfiguratorMenuOptions
    )
  );
};

export const setAccessDeniedError = (identifier: string) => ({
  type: ActionTypes.SET_ACCESS_DENIED_ERROR,
  payload: identifier,
});

export const clearAccessDeniedError = () => ({
  type: ActionTypes.CLEAR_ACCESS_DENIED_ERROR,
});

export const loadLanguages = () => (dispatch) => {
  dispatch(apiActions.fetchData(`languages`, setLanguages));
};

export const loadCountries = () => (dispatch) => {
  dispatch(apiActions.fetchData(`countries`, setCountries));
};

export const setUserFlags = (payload: UserFlags) => ({
  type: ActionTypes.SET_USER_FLAGS,
  payload,
});

export const toNextStep = () => (dispatch, getState) => dispatch(setCurrentStep(getNextStep(getState())));

export const loadProductParameterForm = () =>
  withGlobalLoader(async (dispatch, getState) => {
    const productIdentifier = getSelectedProductType(getState())?.identifier;
    const productFormData = getProductParameterFormData(getState(), productIdentifier);
    if (productFormData) {
      return;
    }
    const url = `product_parameter_forms/${productIdentifier}.json`;
    cachedPromiseCreator(url, async () => {
      try {
        const result = await Api.request({
          baseURL: '',
          url,
        });
        const { data: formData } = result;
        if (typeof formData !== 'object') {
          console.error(`Wrong form data format: ${formData}`);
          return;
        }
        dispatch({
          type: ActionTypes.SET_PRODUCT_PARAMETER_FORM_DATA,
          productIdentifier,
          formData,
        });
      } catch (e) {
        console.error(`Error while loading product parameter form: ${url}`, e);
      }
    });
  });
